import {
  useChangeFunctions,
  ApproverGroup,
  ChangeFunctionApproverGroupApprovalRequirement
} from '@domain/shifthandover'
import {DataContentWrapper} from '@hconnect/common/containers'
import {Check, Close} from '@mui/icons-material'
import {LoadingButton} from '@mui/lab'
import {Box, Button, useMediaQuery, Theme} from '@mui/material'
import {UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Dispatch, FC, SetStateAction} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../../consts'

import {ApproverList} from './ApproverList'
import {ApproverMatrixTable} from './ApproverMatrixTable'

type ApproverMatrixProps = {
  changeFunctionApproverGroupApprovalRequirementsQuery: UseQueryResult<
    ChangeFunctionApproverGroupApprovalRequirement[],
    AxiosError
  >
  data: ChangeFunctionApproverGroupApprovalRequirement[]
  setData: Dispatch<SetStateAction<ChangeFunctionApproverGroupApprovalRequirement[]>>
  onClose: () => void
  handleSave: () => void
  isSaving: boolean
  approverGroupsQuery: UseQueryResult<ApproverGroup[], AxiosError>
}

export const ApproverMatrix: FC<ApproverMatrixProps> = ({
  changeFunctionApproverGroupApprovalRequirementsQuery: {
    data: changeFunctionApproverGroupApprovalRequirements = [],
    isLoading: isMatrixDataLoading,
    isError: isMatrixDataError,
    refetch: refetchMatrixData
  },
  data,
  setData,
  onClose,
  handleSave,
  isSaving,
  approverGroupsQuery: {
    data: approverGroups = [],
    isLoading: isApproverGroupsLoading,
    isError: isApproverGroupsError,
    refetch: refetchApproverGroups
  }
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const {
    data: functions = [],
    isLoading: isFunctionsLoading,
    isError: isFunctionsError,
    refetch: refetchFunctions
  } = useChangeFunctions({language})
  const isDataLoading = isMatrixDataLoading || isApproverGroupsLoading || isFunctionsLoading
  const isDataError = isMatrixDataError || isFunctionsError || isApproverGroupsError

  const refetch = () => {
    if (isMatrixDataError) void refetchMatrixData()
    if (isApproverGroupsError) void refetchApproverGroups()
    if (isFunctionsError) void refetchFunctions()
  }

  return (
    <>
      <DataContentWrapper
        showLoader={isDataLoading}
        data={approverGroups && functions && changeFunctionApproverGroupApprovalRequirements}
        isError={isDataError}
        progressSx={{color: 'primary.main'}}
        retryFunction={refetch}
      >
        {isMobile ? (
          <ApproverList
            functions={functions}
            approverGroups={approverGroups}
            data={data}
            onChange={setData}
          />
        ) : (
          <ApproverMatrixTable
            functions={functions}
            approverGroups={approverGroups}
            data={data}
            onChange={setData}
          />
        )}
      </DataContentWrapper>
      <Box
        display="flex"
        flexDirection={{xs: 'column', md: 'row'}}
        justifyContent="flex-end"
        mt={1.5}
        gap={1}
      >
        {isMobile && (
          <Button
            onClick={onClose}
            variant="outlined"
            data-test-id="approver-matrix-close"
            fullWidth={isMobile}
            color="secondary"
            startIcon={<Close />}
          >
            {t('approversAdjust.action.close')}
          </Button>
        )}
        <LoadingButton
          loading={isSaving}
          onClick={handleSave}
          variant="contained"
          data-test-id="approver-matrix-save"
          fullWidth={isMobile}
          startIcon={<Check />}
        >
          {t('approversAdjust.action.done')}
        </LoadingButton>
      </Box>
    </>
  )
}
